import DSBox from '../../../design-system/DSBox';
import CollectionsGrid from './CollectionGrid';
import { useNavigate } from 'react-router-dom';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import { useTheme } from '@mui/material';
import DSTabGradientViewPaper from '../../../design-system/Paper/DSTabGradientViewPaper';
import useDeviceType from '../../../hooks/useDeviceType';
import useCollectionsList from '../hooks/useCollectionsList';
import { generateObjectId } from '../../../utils';
import { ReactComponent as PlusIcon } from '../../../design-system/Icons/PlusIcon.svg';
import DSTabButton from '../../../design-system/DSTabButton';
import {
  collectionFilterTabs,
  CollectionFilterTab,
} from '../types/filterTypes';
import { DSTypography } from '../../../design-system/Typography';
import loading from '../../../images/loading.png';
import elephant from '../../../images/new-collection.png';
import { KLYDO_WHITE } from '../../../consts/colors';
import useAnalytics from '../../../hooks/useAnalytics';

const Collections = () => {
  const navigate = useNavigate();
  const { spacing } = useTheme();
  const { isMobile } = useDeviceType();
  const {
    data: collections,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    activeFilter,
    handleFilterChange,
  } = useCollectionsList();
  const { trackEvent } = useAnalytics();

  const handleCreateClick = () => {
    const createdObjectId = generateObjectId();
    trackEvent({
      type: 'collection_created',
      context: `collectionId:${createdObjectId}`,
    });
    navigate(`/collection/edit/${createdObjectId}`);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <DSBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="70vh"
        >
          <img
            src={loading}
            alt="Logo"
            width={spacing(26)}
            height={spacing(27)}
          />
          <DSTypography variant="body1" fontSize={spacing(2.5)}>
            Loading...
          </DSTypography>
        </DSBox>
      );
    }

    if (!collections?.length) {
      if (
        activeFilter === CollectionFilterTab.All ||
        activeFilter === CollectionFilterTab.Draft
      ) {
        return (
          <DSBox
            sx={{
              maxWidth: { xxs: '90%', md: '40%' },
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginTop: spacing(2),
              height: '80vh',
            }}
          >
            <DSTypography
              fontFamily="Gilroy-Regular"
              color={KLYDO_WHITE}
              fontSize={{ xxs: '14px', md: '16px', lg: '18px', xl: '20px' }}
              sx={{ gap: spacing(3), display: 'flex', flexDirection: 'column' }}
            >
              <DSBox>
                Collections expand the Klydo universe. Each Collection is a
                curated set of Klydos that introduces original themes and fresh
                creative concepts to Klydoclock.
              </DSBox>
              <DSBox>
                Users can purchase Collections and add them to their personal
                content library, unlocking new revenue opportunities for
                creators.
              </DSBox>
              <DSBox
                onClick={() => handleCreateClick()}
                sx={{
                  textDecorationLine: 'underline',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                Create a Collection today!
              </DSBox>
            </DSTypography>

            <DSBox sx={{ marginTop: spacing(3) }}>
              <img src={elephant} width={spacing(17)} height={spacing(15)} />
            </DSBox>
          </DSBox>
        );
      }

      return (
        <DSBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="70vh"
        >
          <img
            src={loading}
            alt="Logo"
            width={spacing(26)}
            height={spacing(27)}
          />
          <DSTypography variant="body1" fontSize={spacing(2.5)}>
            There are no {activeFilter} collections
          </DSTypography>
        </DSBox>
      );
    }

    return <CollectionsGrid collections={collections} />;
  };

  return (
    <DSTabGradientViewPaper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '87vh',
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingY: spacing(3),
          flexWrap: 'wrap',
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            gap: spacing(1),
            flexDirection: 'row',
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: {
              xxs: '100%',
              xs: '50%',
              sm: '60%',
              md: '70%',
              lg: '80%',
            },
            paddingBottom: spacing(0.5),
            '&::-webkit-scrollbar': {
              height: '4px',
            },
          }}
        >
          {collectionFilterTabs.map((tab) => (
            <DSTabButton
              key={tab}
              title={tab}
              isSelected={activeFilter === tab}
              onClick={() => handleFilterChange(tab)}
            />
          ))}
        </DSBox>
        {!isMobile && (
          <DSPrimaryButton
            endIcon={<PlusIcon />}
            onClick={() => handleCreateClick()}
          >
            Create
          </DSPrimaryButton>
        )}
      </DSBox>
      <DSBox
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          overflowY: 'scroll',
          overflowX: 'hidden',
          flexWrap: 'wrap',
          paddingRight: { lg: spacing(5) },
        }}
      >
        {renderContent()}
      </DSBox>
    </DSTabGradientViewPaper>
  );
};

export default Collections;
